import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Sort } from 'iconoir-react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useTheme } from 'components/core/theme-provider';
import { Checkbox } from 'components/ui/checkbox';
import { useCreditAccess, useSetCreditAccess } from 'features/credits/hooks/use-credits';
import { useGetUsersQuery } from 'features/iam/hooks/use-iam';
import { AddSeatDialog } from 'features/iam/components/seats/add-seat-dialog';
import { SeatPlanCell } from 'features/iam/components/seats/seat-plan-cell';
import { useGivePlan } from 'features/iam/components/seats/use-give-plan';
import { usePendingSeatSettlement } from 'features/subscription/hooks/use-pending-seats';
import { usePlanSeats } from 'features/subscription/hooks/use-seats';
import { planSeatsOfUser, type PlanSeats } from 'features/subscription/utils/seat-pools';
import { useDebounce } from 'hooks/use-debounce';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { type TeamsMember } from '../teams-mock-data';
import {
  isOnOrBeforeRangeEnd,
  mapIamUserToTeamsMember,
  teamsPeopleApiFilter,
  type TeamsPeopleSearchMode,
} from '../map-iam-user-to-teams-member';
import { TeamsCreditAccessSwitch } from './teams-credit-access-switch';
import { TeamsFreeToAssign } from './teams-free-to-assign';
import { TeamsMemberActionsMenu } from './teams-member-actions-menu';
import { MemberNameCell, TeamsMemberStatusBadge, TeamsPeopleCard } from './teams-people-card';
import {
  applyTeamsPeopleLocalFilters,
  teamsPeopleFiltersAreActive,
  TEAMS_PEOPLE_FILTERS_DEFAULT,
  type TeamsPeopleFilters,
} from './teams-people-filter';
import { TeamsPeopleBulkBar } from './teams-people-bulk-bar';
import { TeamsPeoplePagination } from './teams-people-pagination';
import { TeamsPeopleSkeletonCards, TeamsPeopleSkeletonRows } from './teams-people-skeleton';
import { TeamsPeopleToolbar } from './teams-people-toolbar';
import {
  TEAMS_PEOPLE_COLUMN_DEFAULTS,
  type TeamsPeopleColumnVisibility,
} from './teams-view-columns-popover';
import {
  teamsOutlineCardButtonClassName,
  teamsPlanSelectClassName,
  teamsTableBodyRowClassName,
  teamsTableHeaderRowClassName,
  teamsTableShellClassName,
} from './teams-styles';

type TeamsSortColumn = 'name' | 'lastLogin' | 'joinedOn' | 'status';
type TeamsSortDirection = 'asc' | 'desc' | null;

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

// Table columns mapped to the user fields the getUsers API sorts by (same as the IAM table).
const SORT_PROPERTY_BY_COLUMN: Record<TeamsSortColumn, string> = {
  name: 'FirstName',
  lastLogin: 'LastLoggedInTime',
  joinedOn: 'CreatedDate',
  status: 'Active',
};

function SortHeader({
  label,
  className,
  direction = null,
  onSort,
}: {
  label: string;
  className?: string;
  direction?: TeamsSortDirection;
  onSort?: () => void;
}) {
  const labelClassName = cn(
    'inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium leading-body',
    className
  );
  if (!onSort) return <span className={labelClassName}>{label}</span>;

  let SortIcon = Sort;
  if (direction === 'asc') SortIcon = ArrowUp;
  if (direction === 'desc') SortIcon = ArrowDown;

  return (
    <button type="button" onClick={onSort} className={labelClassName}>
      {label}
      <SortIcon
        width={13}
        height={13}
        strokeWidth={1.5}
        className={cn('shrink-0', !direction && 'opacity-70')}
        aria-hidden
      />
    </button>
  );
}

export function TeamsPeopleTab() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isAdmin = useCommonStore((state) => state.isAdmin);
  const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);
  const currentUserId = useCommonStore((state) => state.user?.id);
  const [query, setQuery] = useState('');
  const [searchMode, setSearchMode] = useState<TeamsPeopleSearchMode>('name');
  const [statuses, setStatuses] = useState<string[]>([]);
  const [joinedOnRange, setJoinedOnRange] = useState<DateRange | undefined>();
  const [lastLoginRange, setLastLoginRange] = useState<DateRange | undefined>();
  const [sort, setSort] = useState<{ column: TeamsSortColumn; isDescending: boolean } | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<TeamsPeopleFilters>(TEAMS_PEOPLE_FILTERS_DEFAULT);
  // Keyed by id and kept across pages, so "Give them a plan" has every selected person's name.
  const [selected, setSelected] = useState<Record<string, TeamsMember>>({});
  const selectedMembers = Object.values(selected);
  const [columnVisibility, setColumnVisibility] =
    useState<TeamsPeopleColumnVisibility>(TEAMS_PEOPLE_COLUMN_DEFAULTS);
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
  // Plans come from the organization's seat pools, the same source as the IAM table's Plan column.
  // Read again each time the tab opens, so plans changed elsewhere show up.
  const { planSeats } = usePlanSeats(true);
  usePendingSeatSettlement();
  const { givePlan, isPending: isMovingSeats, dialog: seatDialog } = useGivePlan(planSeats);
  const memberPlan = (member: TeamsMember) => planSeatsOfUser(planSeats, member.id);
  const handleGivePlan = (members: TeamsMember[], entry: PlanSeats) =>
    givePlan(
      members.map((member) => ({ userId: member.id, name: member.name })),
      entry,
      () => setSelected({})
    );
  // Keeps the Teams plan control shape on the IAM picker (`!` because cn does not group rounded-card).
  const planTriggerClassName = (member: TeamsMember) =>
    cn(
      teamsPlanSelectClassName,
      '!rounded-card border-solid py-0 font-normal',
      borderColor,
      memberPlan(member) ? valueColor : mutedColor
    );

  const titleColor = theme === 'dark' ? 'text-true-white' : 'text-grey-950';
  const mutedColor = theme === 'dark' ? 'text-grey-400' : 'text-grey-500';
  const valueColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const headerColor = theme === 'dark' ? 'text-grey-300' : 'text-grey-600';
  const borderColor = theme === 'dark' ? 'border-border' : 'border-grey-100';

  const apiFilter = teamsPeopleApiFilter({
    query: debouncedQuery,
    searchMode,
    statuses,
    joinedOn: joinedOnRange,
    lastLogin: lastLoginRange,
  });

  const { data, isLoading, isFetching, error } = useGetUsersQuery({
    page,
    pageSize,
    filter: apiFilter,
    sort: sort
      ? { property: SORT_PROPERTY_BY_COLUMN[sort.column], isDescending: sort.isDescending }
      : undefined,
    enabled: isAdmin || isOrgAdmin,
  });

  const members = useMemo(
    () =>
      (data?.data ?? [])
        .filter(
          (user) =>
            isOnOrBeforeRangeEnd(user.createdDate, joinedOnRange) &&
            isOnOrBeforeRangeEnd(user.lastLoggedInTime, lastLoginRange)
        )
        .map((user) => mapIamUserToTeamsMember(user, currentUserId)),
    [currentUserId, data?.data, joinedOnRange, lastLoginRange]
  );

  // Everyone may use the shared credits unless an admin switched them off.
  const memberIds = useMemo(() => members.map((member) => member.id), [members]);
  const { data: creditAccess } = useCreditAccess(memberIds, isAdmin || isOrgAdmin);
  const setCreditAccess = useSetCreditAccess();
  const creditAccessById = useMemo(() => {
    const blocked = new Set(creditAccess?.blockedUserIds);
    return Object.fromEntries(members.map((member) => [member.id, !blocked.has(member.id)]));
  }, [creditAccess, members]);
  const changeCreditAccess = (userId: string, allowed: boolean) =>
    setCreditAccess.mutate({ userId, allowed });

  useEffect(() => {
    setPage(0);
  }, [debouncedQuery, searchMode, statuses, joinedOnRange, lastLoginRange]);

  const filteredMembers = applyTeamsPeopleLocalFilters(
    members,
    filters,
    (member) => planSeatsOfUser(planSeats, member.id)?.code ?? null,
    creditAccessById
  );
  // The row menu acts on the full IAM user (details sheet, edit profile, reset password).
  const usersById = Object.fromEntries((data?.data ?? []).map((user) => [user.itemId, user]));

  const totalCount = data?.totalCount ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageStart = totalCount === 0 ? 0 : safePage * pageSize;
  const visibleMembers = filteredMembers;
  const rangeEnd = totalCount === 0 ? 0 : pageStart + visibleMembers.length;
  const selectedOnPage = visibleMembers.filter((member) => selected[member.id]).length;
  // Like the IAM table: a partly selected page shows the header checkbox as indeterminate.
  let pageSelection: boolean | 'indeterminate' = false;
  if (selectedOnPage > 0) pageSelection = 'indeterminate';
  if (visibleMembers.length > 0 && selectedOnPage === visibleMembers.length) pageSelection = true;
  const showLoading = isLoading || (isFetching && visibleMembers.length === 0);
  const isFiltered =
    query.trim() !== '' ||
    statuses.length > 0 ||
    joinedOnRange != null ||
    lastLoginRange != null ||
    teamsPeopleFiltersAreActive(filters);

  const sortDirection = (column: TeamsSortColumn): TeamsSortDirection => {
    if (sort?.column !== column) return null;
    return sort.isDescending ? 'desc' : 'asc';
  };

  // Same cycle as the IAM table: ascending, then descending, then off.
  const sortHandler = (column: TeamsSortColumn) => () => {
    setPage(0);
    setSort((current) => {
      if (current?.column !== column) return { column, isDescending: false };
      if (!current.isDescending) return { column, isDescending: true };
      return null;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelected((current) => {
      const next = { ...current };
      visibleMembers.forEach((member) => {
        if (checked) next[member.id] = member;
        else delete next[member.id];
      });
      return next;
    });
  };

  const toggleSelect = (member: TeamsMember, checked: boolean) => {
    setSelected((current) => {
      const next = { ...current };
      if (checked) next[member.id] = member;
      else delete next[member.id];
      return next;
    });
  };

  const clearSearchAndFilters = () => {
    setQuery('');
    setStatuses([]);
    setJoinedOnRange(undefined);
    setLastLoginRange(undefined);
    setFilters(TEAMS_PEOPLE_FILTERS_DEFAULT);
    setPage(0);
  };

  const showNoResults = !showLoading && !error && visibleMembers.length === 0;
  const tableColumnCount =
    4 +
    (columnVisibility.creditAccess ? 1 : 0) +
    (columnVisibility.lastLogin ? 1 : 0) +
    (columnVisibility.joinedOn ? 1 : 0) +
    (columnVisibility.status ? 1 : 0);

  const noResultsContent = (
    <div className="flex flex-col items-center gap-2.5">
      <p className={cn('text-center text-sm leading-body', headerColor)}>
        {t('TEAMS_PEOPLE_NO_MATCH', {
          defaultValue: 'No one matches that search or filter.',
        })}
      </p>
      <button
        type="button"
        onClick={clearSearchAndFilters}
        className={cn(teamsOutlineCardButtonClassName, valueColor, borderColor)}
      >
        {t('TEAMS_PEOPLE_CLEAR_SEARCH_FILTERS', {
          defaultValue: 'Clear search and filters',
        })}
      </button>
    </div>
  );

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <TeamsFreeToAssign />

      {selectedMembers.length > 0 && (
        <TeamsPeopleBulkBar
          count={selectedMembers.length}
          planSeats={planSeats}
          disabled={isMovingSeats}
          onPick={(entry) => handleGivePlan(selectedMembers, entry)}
          onClear={() => setSelected({})}
        />
      )}

      <TeamsPeopleToolbar
        query={query}
        onQueryChange={(next) => {
          setQuery(next);
          setPage(0);
        }}
        searchMode={searchMode}
        onSearchModeChange={setSearchMode}
        statuses={statuses}
        onStatusesChange={setStatuses}
        joinedOn={joinedOnRange}
        onJoinedOnChange={setJoinedOnRange}
        lastLogin={lastLoginRange}
        onLastLoginChange={setLastLoginRange}
        filters={filters}
        onFiltersChange={(next) => {
          setFilters(next);
          setPage(0);
        }}
        planOptions={planSeats.map((entry) => ({ code: entry.code, name: entry.name }))}
        isFiltered={isFiltered}
        onReset={clearSearchAndFilters}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
      />

      {error && !showLoading && (
        <p className={cn('text-sm leading-body', mutedColor)} role="alert">
          {t('TEAMS_PEOPLE_ERROR', { defaultValue: 'Could not load people. Try again.' })}
        </p>
      )}

      {showNoResults && (
        <div
          className={cn(
            'flex flex-col items-center rounded-section border bg-true-white px-4 py-12 md:hidden dark:bg-card',
            borderColor
          )}
        >
          {noResultsContent}
        </div>
      )}

      {/* Mobile cards */}
      <ul
        className={cn(
          'flex flex-col gap-2 md:hidden',
          ((error && !showLoading) || showNoResults) && 'hidden'
        )}
        aria-busy={showLoading}
      >
        {showLoading && (
          <TeamsPeopleSkeletonCards columnVisibility={columnVisibility} borderColor={borderColor} />
        )}
        {visibleMembers.map((member) => (
          <TeamsPeopleCard
            key={member.id}
            member={member}
            isSelected={Boolean(selected[member.id])}
            onSelectedChange={(checked) => toggleSelect(member, checked)}
            columnVisibility={columnVisibility}
            plan={
              <SeatPlanCell
                current={memberPlan(member)}
                planSeats={planSeats}
                disabled={isMovingSeats}
                triggerClassName={cn(
                  planTriggerClassName(member),
                  'w-full min-w-0 max-w-full whitespace-normal'
                )}
                onPick={(entry) => handleGivePlan([member], entry)}
              />
            }
            creditAccess={
              <TeamsCreditAccessSwitch
                checked={creditAccessById[member.id] ?? true}
                memberName={member.name}
                creditsUsed={member.creditsUsed}
                mutedColor={mutedColor}
                onCheckedChange={(checked) => changeCreditAccess(member.id, checked)}
              />
            }
            actions={<TeamsMemberActionsMenu user={usersById[member.id]} memberName={member.name} />}
          />
        ))}
      </ul>

      {/* Desktop table */}
      <div
        className={cn(
          'hidden md:block',
          teamsTableShellClassName,
          error && !showLoading && 'md:hidden'
        )}
        aria-busy={showLoading}
      >
        {/* overflow-x-auto turns on vertical scrolling too; a 1px rounding overflow then shows a scrollbar. */}
        <div className="w-full max-w-full overflow-x-auto overflow-y-hidden">
          <table className="w-full min-w-max border-collapse text-left">
            <thead>
              <tr className={cn(teamsTableHeaderRowClassName, borderColor)}>
                <th className="w-10 px-4 py-3">
                  <Checkbox
                    checked={pageSelection}
                    onCheckedChange={(checked) => toggleSelectAll(checked === true)}
                    aria-label={t('TEAMS_SELECT_EVERYONE', {
                      defaultValue: 'Select everyone on this page',
                    })}
                  />
                </th>
                <th className="px-4 py-3">
                  <SortHeader
                    label={t('TEAMS_COL_NAME', { defaultValue: 'Name' })}
                    className={titleColor}
                    direction={sortDirection('name')}
                    onSort={sortHandler('name')}
                  />
                </th>
                {/* Plan and credit access are not API sort fields, same as the IAM Plan column. */}
                <th className="px-4 py-3">
                  <SortHeader label={t('TEAMS_COL_PLAN', { defaultValue: 'Plan' })} className={headerColor} />
                </th>
                {columnVisibility.creditAccess && (
                  <th className="px-4 py-3">
                    <SortHeader
                      label={t('TEAMS_COL_CREDIT_ACCESS', { defaultValue: 'Credit access' })}
                      className={headerColor}
                    />
                  </th>
                )}
                {columnVisibility.lastLogin && (
                  <th className="px-4 py-3">
                    <SortHeader
                      label={t('TEAMS_COL_LAST_LOGIN', { defaultValue: 'Last login' })}
                      className={headerColor}
                      direction={sortDirection('lastLogin')}
                      onSort={sortHandler('lastLogin')}
                    />
                  </th>
                )}
                {columnVisibility.joinedOn && (
                  <th className="px-4 py-3">
                    <SortHeader
                      label={t('TEAMS_COL_JOINED_ON', { defaultValue: 'Joined on' })}
                      className={headerColor}
                      direction={sortDirection('joinedOn')}
                      onSort={sortHandler('joinedOn')}
                    />
                  </th>
                )}
                {columnVisibility.status && (
                  <th className="px-4 py-3">
                    <SortHeader
                      label={t('TEAMS_COL_STATUS', { defaultValue: 'Status' })}
                      className={headerColor}
                      direction={sortDirection('status')}
                      onSort={sortHandler('status')}
                    />
                  </th>
                )}
                <th className="w-10 px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {showNoResults && (
                <tr>
                  <td colSpan={tableColumnCount} className="px-4 py-12">
                    {noResultsContent}
                  </td>
                </tr>
              )}
              {showLoading && (
                <TeamsPeopleSkeletonRows
                  columnVisibility={columnVisibility}
                  borderColor={borderColor}
                />
              )}
              {visibleMembers.map((member) => (
                    <tr key={member.id} className={cn(teamsTableBodyRowClassName, borderColor)}>
                      <td className="px-4 py-4 align-middle">
                        <Checkbox
                          checked={Boolean(selected[member.id])}
                          onCheckedChange={(checked) =>
                            toggleSelect(member, checked === true)
                          }
                          aria-label={t('TEAMS_SELECT_MEMBER', {
                            defaultValue: 'Select {{name}}',
                            name: member.name,
                          })}
                        />
                      </td>
                      <td className="max-w-xs px-4 py-3.5 align-middle">
                        <MemberNameCell
                          member={member}
                          nameColor={titleColor}
                          mutedColor={mutedColor}
                        />
                      </td>
                      <td className="px-4 py-3.5 align-middle">
                        <SeatPlanCell
                          current={memberPlan(member)}
                          planSeats={planSeats}
                          disabled={isMovingSeats}
                          triggerClassName={planTriggerClassName(member)}
                          onPick={(entry) => handleGivePlan([member], entry)}
                        />
                      </td>
                      {columnVisibility.creditAccess && (
                        <td className="px-4 py-4 align-middle">
                          <TeamsCreditAccessSwitch
                            checked={creditAccessById[member.id] ?? true}
                            memberName={member.name}
                            creditsUsed={member.creditsUsed}
                            mutedColor={mutedColor}
                            onCheckedChange={(checked) => changeCreditAccess(member.id, checked)}
                          />
                        </td>
                      )}
                      {columnVisibility.lastLogin && (
                        <td
                          className={cn(
                            'whitespace-nowrap px-4 py-4 align-middle text-sm leading-body',
                            headerColor
                          )}
                        >
                          {member.lastLogin ?? '—'}
                        </td>
                      )}
                      {columnVisibility.joinedOn && (
                        <td
                          className={cn(
                            'whitespace-nowrap px-4 py-4 align-middle text-sm leading-body',
                            headerColor
                          )}
                        >
                          {member.joinedOn ?? '—'}
                        </td>
                      )}
                      {columnVisibility.status && (
                        <td className="px-4 py-4 align-middle">
                          <TeamsMemberStatusBadge isActive={member.status === 'active'} />
                        </td>
                      )}
                      <td className="px-4 py-4 align-middle">
                        <TeamsMemberActionsMenu
                          user={usersById[member.id]}
                          memberName={member.name}
                        />
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>

      <TeamsPeoplePagination
        page={safePage}
        pageCount={pageCount}
        pageSize={pageSize}
        rangeStart={totalCount === 0 ? 0 : pageStart + 1}
        rangeEnd={rangeEnd}
        totalCount={totalCount}
        isDisabled={showLoading}
        onPageChange={setPage}
        onPageSizeChange={(next) => {
          setPageSize(next);
          setPage(0);
        }}
      />

      <AddSeatDialog {...seatDialog} />
    </div>
  );
}
